import React from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Clock } from "lucide-react";
import { IsolatedStageTimer } from "@/components/isolated-stage-timer";
import { formatTimer } from "@/lib/timer-utils";
import ParticipantActionsDropdown from "@/components/participant-actions-dropdown";
import type { Performer, JamNight } from "@shared/schema";
import type { ParticipantMode, ModeConfig } from "./mode-config";
import { 
  ParticipantBadge, 
  InstrumentBadges, 
  TypeBadge, 
  TierBadge, 
  PriorityBadge, 
  UpNextBadge 
} from "./participant-badge";
import { ParticipantActions } from "./participant-actions";

interface ParticipantDesktopRowProps {
  performer: Performer;
  mode: ParticipantMode;
  config: ModeConfig;
  onActionToggle: (performer: Performer) => void;
  onConsider?: (performer: Performer) => void;
  onRemove: (performerId: number) => void;
  isLoading: boolean;
  disabled?: boolean;
  jamNightId?: number;
  jamNight?: JamNight;
  isFirst?: boolean;
}

export const ParticipantDesktopRow = React.memo(function ParticipantDesktopRow({
  performer,
  mode,
  config,
  onActionToggle,
  onConsider,
  onRemove,
  isLoading,
  disabled = false,
  jamNightId,
  jamNight,
  isFirst = false,
}: ParticipantDesktopRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: performer.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? 'none' : transition,
    opacity: isDragging ? 0.5 : 1,
    willChange: isDragging ? 'transform' : 'auto',
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`${isDragging 
        ? "bg-white border border-slate-300 shadow-lg z-50" 
        : "hover:bg-white/20 backdrop-blur-sm"
      }`}
    >
      {/* Name Column */}
      <td className="px-6 py-4">
        <div className="flex items-center">
          <div
            {...(disabled ? {} : attributes)}
            {...(disabled ? {} : listeners)}
            className={`mr-3 text-slate-400 ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-grab active:cursor-grabbing hover:text-slate-600'}`}
          >
            <GripVertical className="h-4 w-4" />
          </div>
          <span className="text-lg font-medium text-slate-900/90 break-words drop-shadow-sm">
            {performer.name}
            {config.isOpenMic && isFirst && <UpNextBadge isDragging={isDragging} />}
          </span>
        </div>
      </td>

      {/* Instruments Column (Jam Night only) */}
      {config.isJamNight && (
        <td className="px-6 py-4">
          <InstrumentBadges instruments={performer.instruments} isDragging={isDragging} />
        </td>
      )}

      {/* Type Column (Open Mic only) */}
      {config.isOpenMic && (
        <td className="px-6 py-4 whitespace-nowrap">
          <TypeBadge performerType={performer.performerType} isDragging={isDragging} />
        </td>
      )}

      {/* Tier Column (Jam Night only) */}
      {config.isJamNight && (
        <td className="px-6 py-4">
          <div className="flex flex-wrap gap-1">
            <TierBadge tier={performer.tier} />
          </div>
        </td>
      )}

      {/* Priority Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <PriorityBadge priority={performer.priority} isDragging={isDragging} />
      </td>

      {/* Played/Attended Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center">
          <ParticipantBadge performer={performer} mode={mode} isDragging={isDragging} />
        </div>
      </td>

      {/* Performance Count Column (Jam Night only) */}
      {config.isJamNight && (
        <td className="px-6 py-4 whitespace-nowrap">
          <span className="font-medium text-slate-900/90 drop-shadow-sm">
            {performer.performanceCount || 0}
          </span>
        </td>
      )}

      {/* Time on Stage Column (Jam Night and Open Mic) */}
      {!config.isGenericEvent && (
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center space-x-2">
            {config.isJamNight ? (
              <>
                <span className="font-medium text-slate-900/90 drop-shadow-sm">
                  {performer.timeOnStage} min
                </span>
                {(performer.status === 'on_stage' || performer.status === 'pending_removal') && (
                  <div className="flex items-center text-warning">
                    <Clock className="h-3 w-3 mr-1" />
                    <IsolatedStageTimer 
                      stageStartTime={performer.stageStartTime}
                      className="font-mono font-medium"
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-slate-400/80 mr-2 drop-shadow-sm" />
                <span className="text-sm text-slate-600/90 drop-shadow-sm">
                  {formatTimer((performer.timeOnStage || 0) * 60)}
                </span>
              </div>
            )}
          </div>
        </td>
      )}

      {/* Actions Column */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center space-x-2">
          <ParticipantActions
            performer={performer}
            mode={mode}
            onActionToggle={onActionToggle}
            onConsider={onConsider}
            disabled={disabled}
            isLoading={isLoading}
            isDragging={isDragging}
          />
          <ParticipantActionsDropdown
            performer={performer}
            onRemove={onRemove}
            disabled={disabled}
            isLoading={isLoading}
            jamNight={jamNight}
            jamNightId={jamNightId}
            isDragging={isDragging}
          />
        </div>
      </td>
    </tr>
  );
});